import React, { useEffect, useState } from "react";
import { Loader2, Save, BookOpen, PenLine } from "lucide-react";
import { toast } from "sonner";
import api, { todayKey, prettyDate } from "@/lib/api";

const Journal = () => {
  const dateKey = todayKey();
  const [entry, setEntry] = useState(null);
  const [journals, setJournals] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [todayRes, listRes, existingRes] = await Promise.allSettled([
        api.get("/entries/today"),
        api.get("/journals"),
        api.get(`/journals/${dateKey}`),
      ]);
      let questions = [];
      if (todayRes.status === "fulfilled") {
        setEntry(todayRes.value.data);
        questions = todayRes.value.data.integration_questions || [];
      }
      if (listRes.status === "fulfilled") setJournals(Array.isArray(listRes.value.data) ? listRes.value.data : []);
      if (existingRes.status === "fulfilled" && existingRes.value.data && existingRes.value.data.date) {
        const ex = existingRes.value.data;
        setNotes(ex.notes || "");
        setAnswers(questions.map((_, i) => (ex.answers && ex.answers[i] && ex.answers[i].answer) || ""));
      } else {
        setAnswers(questions.map(() => ""));
      }
    } catch (e) {
      toast.error("Could not load your journal");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/journals", {
        date: dateKey,
        answers: (entry?.integration_questions || []).map((q, i) => ({ question: q, answer: (answers[i] || "").trim() })),
        notes,
      });
      toast.success("Journal entry saved");
      const { data } = await api.get("/journals");
      setJournals(Array.isArray(data) ? data : []);
    } catch (err) { toast.error("Could not save your entry"); }
    finally { setSaving(false); }
  };

  const questions = entry?.integration_questions || [];
  const ta = "w-full px-4 py-2.5 rounded-lg border border-input bg-secondary/40 outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none transition-shadow";

  return (
    <div className="px-4 sm:px-6 py-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground">Journal</h1>
          <p className="text-muted-foreground mt-2">A private logbook for your thoughts — {prettyDate(dateKey)}.</p>
        </div>
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <>
            <form onSubmit={save} className="bg-card border border-border rounded-2xl p-6 shadow-sm mb-10">
              <div className="flex items-center gap-2 mb-5 text-primary"><PenLine className="w-4 h-4" /><span className="text-sm font-medium">Today's entry</span></div>
              {questions.length > 0 && (
                <div className="space-y-5 mb-5">
                  {questions.map((q, i) => (
                    <div key={i}>
                      <label className="text-sm font-medium text-foreground/90 block mb-1.5">{q}</label>
                      <textarea data-testid={`journal-answer-${i}`} rows={2} value={answers[i] || ""}
                        onChange={(e) => setAnswers((prev) => { const n = [...prev]; n[i] = e.target.value; return n; })}
                        placeholder="Your reflection…" className={ta} />
                    </div>
                  ))}
                </div>
              )}
              <label className="text-sm font-medium text-foreground/90 block mb-1.5">Free-form notes</label>
              <textarea data-testid="journal-notes-input" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything else on your mind today…" className={ta} />
              <div className="flex justify-end mt-4">
                <button data-testid="journal-save-button" type="submit" disabled={saving}
                  className="flex items-center gap-2 bg-primary text-primary-foreground text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-atlantic active:scale-95 transition-all disabled:opacity-50">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save entry
                </button>
              </div>
            </form>
            <h2 className="font-heading text-2xl font-semibold mb-4">Past entries</h2>
            {journals.length === 0 ? (
              <p className="text-muted-foreground py-6">Your logbook is empty. Save today's entry to begin.</p>
            ) : (
              <div className="space-y-4" data-testid="journal-list">
                {journals.map((j, idx) => (
                  <div key={j.id || j.date || idx} data-testid="journal-entry-card" className="bg-card border border-border rounded-2xl p-5 shadow-sm">
                    <div className="flex items-center gap-2 mb-3 text-brass"><BookOpen className="w-4 h-4" /><span className="text-sm font-semibold">{prettyDate(j.date)}</span></div>
                    {Array.isArray(j.answers) && j.answers.filter((a) => a && a.answer).length > 0 && (
                      <ul className="space-y-3 mb-3">
                        {j.answers.filter((a) => a && a.answer).map((a, i) => (
                          <li key={i} className="border-l-2 border-primary/40 pl-3">
                            {a.question && <p className="text-xs text-muted-foreground mb-0.5">{a.question}</p>}
                            <p className="text-foreground/80 leading-relaxed">{a.answer}</p>
                          </li>
                        ))}
                      </ul>
                    )}
                    {j.notes && <p className="text-foreground/90 leading-relaxed whitespace-pre-wrap">{j.notes}</p>}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Journal;
